import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { fetchDemItem } from '../../lib/demItems';
import {
  deleteSslTemplate,
  fetchSslLinesByTemplate,
  nextSslLineNo,
  saveSslTemplateLines,
  type DemSslLineRow,
} from '../../lib/demStandardSalesLines';

type DraftLine = {
  key: string;
  line_no: number;
  type: string;
  item_code: string;
  description: string;
  unknown: boolean;
};

type Props = {
  open: boolean;
  /** Existing template to edit, or null when creating. */
  templateCode: string | null;
  isNew: boolean;
  onClose: () => void;
  onSaved: () => void;
  onDeleted: () => void;
};

function newKey(): string {
  return `k-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toDraft(rows: DemSslLineRow[]): DraftLine[] {
  return rows.map((row) => ({
    key: row.id != null ? `id-${row.id}` : newKey(),
    line_no: row.line_no,
    type: row.type || 'Item',
    item_code: row.item_code,
    description: row.description,
    unknown: row.unknown,
  }));
}

export function StandardSalesLinesEditModal({
  open,
  templateCode,
  isNew,
  onClose,
  onSaved,
  onDeleted,
}: Props) {
  const [codeInput, setCodeInput] = useState('');
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeCode = isNew ? codeInput.trim() : (templateCode ?? '').trim();

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (isNew) {
      setCodeInput('');
      setLines([]);
      setLoading(false);
      return;
    }
    const code = (templateCode ?? '').trim();
    if (!code) return;
    let cancelled = false;
    setLoading(true);
    void fetchSslLinesByTemplate(code)
      .then((rows) => {
        if (!cancelled) setLines(toDraft(rows));
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load lines.');
          setLines([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, templateCode, isNew]);

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    setLines((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const resolveItem = async (key: string, rawCode: string) => {
    const code = rawCode.trim().toUpperCase();
    updateLine(key, { item_code: code });
    if (!code) {
      updateLine(key, { description: '', unknown: true });
      return;
    }
    try {
      const item = await fetchDemItem(code);
      if (item) {
        updateLine(key, {
          item_code: item.code.trim().toUpperCase() || code,
          description: item.description.trim() || 'Unknown',
          unknown: !item.description.trim(),
        });
      } else {
        updateLine(key, { description: 'Unknown', unknown: true });
      }
    } catch {
      updateLine(key, { description: 'Unknown', unknown: true });
    }
  };

  const handleAddLine = () => {
    setLines((prev) => [
      ...prev,
      {
        key: newKey(),
        line_no: nextSslLineNo(prev),
        type: 'Item',
        item_code: '',
        description: '',
        unknown: true,
      },
    ]);
  };

  const handleRemoveLine = (key: string) => {
    setLines((prev) => prev.filter((row) => row.key !== key));
  };

  const handleSave = async () => {
    const code = activeCode.trim().toUpperCase();
    if (!code) {
      setError('Enter a customer / template code.');
      return;
    }
    const payload = lines
      .map((row) => ({
        line_no: Number(row.line_no) || 0,
        type: row.type.trim() || 'Item',
        item_code: row.item_code.trim().toUpperCase(),
      }))
      .filter((row) => row.item_code);
    if (payload.length === 0) {
      setError('Add at least one item line before saving.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveSslTemplateLines(code, payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const code = (templateCode ?? '').trim();
    if (!code || isNew) return;
    if (
      !window.confirm(
        `Delete all standard sales lines for ${code}? Call-in will no longer prefill items for this code.`,
      )
    ) {
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      await deleteSslTemplate(code);
      onDeleted();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete.');
    } finally {
      setDeleting(false);
    }
  };

  const busy = loading || saving || deleting;

  return (
    <Modal
      title={isNew ? 'New standard sales lines' : `Standard sales lines — ${templateCode}`}
      open={open}
      onClose={() => {
        if (!busy) onClose();
      }}
      extraWide
      panelClassName="dem-ssl-edit-modal"
    >
      <div className="dem-ssl-edit">
        <div className="dem-ssl-edit__top">
          {isNew ? (
            <label className="field">
              <span className="field-label">Customer / template code</span>
              <input
                className="field-input"
                value={codeInput}
                disabled={busy}
                placeholder="e.g. A0012"
                autoFocus
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              />
            </label>
          ) : null}
          {error ? <div className="alert alert-error">{error}</div> : null}
        </div>

        {loading ? (
          <p className="field-hint dem-ssl-edit__loading" role="status">
            Loading lines…
          </p>
        ) : (
          <div className="dem-ssl-edit__table-wrap">
            <table className="dem-ssl-edit__table">
              <thead>
                <tr>
                  <th className="dem-ssl-edit__col-line">Line</th>
                  <th className="dem-ssl-edit__col-item">Item</th>
                  <th>Description</th>
                  <th className="dem-ssl-edit__col-actions" />
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="dem-ssl-edit__empty">
                      No lines yet. Add an item below.
                    </td>
                  </tr>
                ) : (
                  lines.map((row) => (
                    <tr key={row.key}>
                      <td>
                        <input
                          className="field-input dem-ssl-edit__line-no"
                          type="text"
                          inputMode="numeric"
                          autoComplete="off"
                          value={row.line_no || ''}
                          disabled={busy}
                          onChange={(e) => {
                            const digits = e.target.value.replace(/\D/g, '');
                            updateLine(row.key, {
                              line_no: digits ? Number(digits) : 0,
                            });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="field-input"
                          value={row.item_code}
                          disabled={busy}
                          placeholder="Item code"
                          onChange={(e) =>
                            updateLine(row.key, {
                              item_code: e.target.value.toUpperCase(),
                            })
                          }
                          onBlur={() => void resolveItem(row.key, row.item_code)}
                        />
                      </td>
                      <td>
                        <span
                          className={`dem-ssl-edit__desc${row.unknown ? ' is-unknown' : ''}`}
                          title={row.description}
                        >
                          {row.description || '—'}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={busy}
                          onClick={() => handleRemoveLine(row.key)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="dem-ssl-edit__footer">
          <div className="dem-ssl-edit__footer-left">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={busy || loading}
              onClick={handleAddLine}
            >
              Add line
            </button>
            {!isNew ? (
              <button
                type="button"
                className="btn-secondary btn-sm"
                disabled={busy}
                onClick={() => void handleDelete()}
              >
                {deleting ? 'Deleting…' : 'Delete template'}
              </button>
            ) : null}
          </div>
          <div className="dem-ssl-edit__footer-right">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || loading}
              onClick={() => void handleSave()}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
